/**
 * AIMatchingService — Calcul de score de matching réaliste entre articles
 *
 * Exploite les `ai_metadata` enrichies par Claude Vision (couleur, formality,
 * styleScores, occasions, pattern, material, gender, seasonality, etc.) +
 * les indices du nom de l'article comme signal de renforcement.
 *
 * Utilisé par :
 *   - Suggestions (matching outfit complet vs contexte)
 *   - Looks (score de compatibilité affiché 0-100%)
 *   - Looks Voyage (score adapté au contexte voyage : météo destination, occasion)
 *   - Catalogue (matching article catalogue vs dressing)
 *
 * Tous les scores sont retournés sur 0..100 et calibrés pour être réalistes :
 *   85+  = excellent match (couleurs harmonieuses, mêmes registre/style)
 *   70-84 = bon match (compatibles avec quelques compromis)
 *   55-69 = match moyen (faisable mais pas optimal)
 *   <55  = mismatch (couleurs ou styles incompatibles)
 */

/* ══════════════════════════════════════════════════════════
   1. EXTRACTION D'INDICES DU NOM
══════════════════════════════════════════════════════════ */

const STYLE_KEYWORDS = {
  chic:        ['chic', 'élégant', 'elegant', 'sophistiqué', 'raffiné'],
  casual:      ['casual', 'décontracté', 'relax', 'cool'],
  romantique:  ['romantique', 'romantic', 'fleurs', 'floral', 'dentelle', 'lace'],
  bohème:      ['bohème', 'boho', 'ethnique', 'gypsy'],
  sport:       ['sport', 'sportif', 'jogging', 'gym', 'running', 'athletic'],
  streetwear:  ['streetwear', 'urban', 'street', 'oversize', 'baggy'],
  minimaliste: ['minimaliste', 'minimal', 'épuré', 'simple', 'basique', 'basic'],
  workwear:    ['workwear', 'bureau', 'business', 'tailleur', 'costume', 'suit'],
  vintage:     ['vintage', 'rétro', 'retro', 'années', 'classique'],
  edgy:        ['edgy', 'rock', 'punk', 'cuir', 'leather', 'noir'],
};

const OCCASION_KEYWORDS = {
  bureau:    ['bureau', 'business', 'office', 'work', 'travail'],
  soirée:    ['soirée', 'evening', 'cocktail', 'gala', 'fête'],
  cocktail:  ['cocktail', 'apéro'],
  weekend:   ['weekend', 'casual', 'décontracté'],
  date:      ['date', 'rendez-vous', 'romantic'],
  sport:     ['sport', 'gym', 'running', 'fitness'],
  vacances:  ['plage', 'beach', 'vacances', 'voyage', 'piscine', 'soleil'],
  cérémonie: ['cérémonie', 'mariage', 'wedding', 'baptême'],
};

/**
 * Extrait des hints de style/occasion à partir du nom + description d'un article.
 * Ne remplace pas l'analyse IA mais sert de fallback ou de renforcement.
 */
function extractHintsFromText(item) {
  if (!item) return { styles: [], occasions: [] };
  const text = `${item.name || ''} ${item.description || ''}`.toLowerCase();
  if (!text.trim()) return { styles: [], occasions: [] };

  const styles = [];
  const occasions = [];

  for (const [style, kws] of Object.entries(STYLE_KEYWORDS)) {
    if (kws.some(kw => text.includes(kw))) styles.push(style);
  }
  for (const [occ, kws] of Object.entries(OCCASION_KEYWORDS)) {
    if (kws.some(kw => text.includes(kw))) occasions.push(occ);
  }
  return { styles, occasions };
}

/* ══════════════════════════════════════════════════════════
   2. ACCÈS UNIFIÉ AUX ATTRIBUTS IA
══════════════════════════════════════════════════════════ */

/**
 * Récupère un attribut depuis `ai_metadata` avec fallback sur les attributs
 * directs de l'item. Permet de gérer transparentement les articles analysés
 * vs non analysés.
 */
function getAttr(item, key) {
  if (!item) return null;
  // ai_metadata = source principale
  const ai = item.ai_metadata || {};
  if (ai[key] !== undefined && ai[key] !== null && ai[key] !== '') return ai[key];
  // fallback : attribut direct
  if (item[key] !== undefined && item[key] !== null && item[key] !== '') return item[key];
  return null;
}

function getStyleScores(item) {
  const ai = item?.ai_metadata || {};
  return ai.styleScores || {};
}

function getOccasionScores(item) {
  const ai = item?.ai_metadata || {};
  return ai.occasions || {};
}

/* ══════════════════════════════════════════════════════════
   3. SCORES PARTIELS (chacun 0..1)
══════════════════════════════════════════════════════════ */

const COLOR_HARMONY = {
  // Couleurs neutres = vont avec tout
  noir:    { neutre: true, complementary: ['blanc', 'gris', 'rouge', 'doré'] },
  blanc:   { neutre: true, complementary: ['noir', 'bleu', 'rouge', 'vert'] },
  gris:    { neutre: true, complementary: ['noir', 'blanc', 'rose', 'jaune'] },
  beige:   { neutre: true, complementary: ['marron', 'noir', 'blanc', 'bleu'] },
  marine:  { neutre: false, complementary: ['blanc', 'beige', 'rouge', 'doré'] },
  bleu:    { neutre: false, complementary: ['blanc', 'beige', 'jaune', 'orange'] },
  marron:  { neutre: false, complementary: ['beige', 'blanc', 'crème', 'kaki'] },
  camel:   { neutre: false, complementary: ['noir', 'blanc', 'marine', 'bordeaux'] },
  kaki:    { neutre: false, complementary: ['blanc', 'beige', 'marine', 'noir'] },
  rouge:   { neutre: false, complementary: ['noir', 'blanc', 'marine', 'gris'] },
  rose:    { neutre: false, complementary: ['blanc', 'gris', 'noir', 'beige'] },
  bordeaux:{ neutre: false, complementary: ['blanc', 'beige', 'noir', 'gris'] },
  vert:    { neutre: false, complementary: ['blanc', 'beige', 'marine', 'jaune'] },
  jaune:   { neutre: false, complementary: ['blanc', 'noir', 'gris', 'marine'] },
  orange:  { neutre: false, complementary: ['blanc', 'marine', 'gris', 'noir'] },
  violet:  { neutre: false, complementary: ['blanc', 'gris', 'noir'] },
  doré:    { neutre: false, complementary: ['noir', 'blanc', 'rouge', 'bleu'] },
  argent:  { neutre: false, complementary: ['noir', 'blanc', 'bleu'] },
};

/**
 * Score d'harmonie de couleurs entre 2 articles (0..1).
 * Logique :
 *  - Même couleur : 0.9
 *  - Une neutre : 0.85
 *  - Complémentaires : 0.8
 *  - Inconnu : 0.5 (neutre)
 *  - Conflit (rouge+rose, vert+rouge vif, etc.) : 0.3
 */
function colorScore(item1, item2) {
  const c1 = (getAttr(item1, 'primaryColor') || getAttr(item1, 'color') || '').toLowerCase();
  const c2 = (getAttr(item2, 'primaryColor') || getAttr(item2, 'color') || '').toLowerCase();

  if (!c1 || !c2) return 0.55; // pas d'info → neutre légèrement bas
  if (c1 === c2) return 0.90;

  const h1 = COLOR_HARMONY[c1];
  const h2 = COLOR_HARMONY[c2];
  if (!h1 || !h2) return 0.55;

  if (h1.neutre || h2.neutre) return 0.85;
  if (h1.complementary?.includes(c2) || h2.complementary?.includes(c1)) return 0.80;

  // Conflits connus
  const conflicts = [
    ['rouge', 'rose'], ['rouge', 'orange'],
    ['vert', 'rouge'], ['violet', 'orange'],
    ['jaune', 'rose'],
  ];
  for (const [a, b] of conflicts) {
    if ((c1 === a && c2 === b) || (c1 === b && c2 === a)) return 0.30;
  }
  return 0.55;
}

/**
 * Score de cohérence formality (0..1).
 * Pénalise les écarts importants : un blazer formality=8 avec un jogging=2 est bizarre.
 */
function formalityScore(item1, item2) {
  const f1 = getAttr(item1, 'formality');
  const f2 = getAttr(item2, 'formality');
  if (f1 == null || f2 == null) return 0.6;
  const diff = Math.abs(Number(f1) - Number(f2));
  if (diff <= 1) return 1.0;
  if (diff <= 2) return 0.85;
  if (diff <= 3) return 0.65;
  if (diff <= 4) return 0.45;
  return 0.25;
}

/**
 * Score de partage de style (0..1).
 * Identifie les styles dominants (score > 60) de chaque article.
 * Si chevauchement → match. Si tous différents → mismatch léger.
 */
function styleScore(item1, item2) {
  const s1 = getStyleScores(item1);
  const s2 = getStyleScores(item2);
  const dom1 = Object.entries(s1).filter(([_, v]) => v >= 60).map(([k]) => k);
  const dom2 = Object.entries(s2).filter(([_, v]) => v >= 60).map(([k]) => k);

  // Renforcement texte
  const hints1 = extractHintsFromText(item1);
  const hints2 = extractHintsFromText(item2);
  const allStyles1 = [...new Set([...dom1, ...hints1.styles])];
  const allStyles2 = [...new Set([...dom2, ...hints2.styles])];

  if (allStyles1.length === 0 || allStyles2.length === 0) return 0.65; // pas d'info → neutre
  const intersection = allStyles1.filter(s => allStyles2.includes(s));
  if (intersection.length >= 2) return 1.0;
  if (intersection.length === 1) return 0.85;

  // Pas d'intersection mais styles peut-être compatibles ?
  const compatibles = {
    chic:        ['élégant', 'minimaliste', 'workwear'],
    casual:      ['streetwear', 'sport', 'minimaliste'],
    romantique:  ['chic', 'bohème', 'vintage'],
    bohème:      ['romantique', 'casual', 'vintage'],
    sport:       ['casual', 'streetwear'],
    streetwear:  ['casual', 'sport', 'edgy'],
    élégant:     ['chic', 'minimaliste', 'workwear'],
    minimaliste: ['chic', 'casual', 'élégant'],
    workwear:    ['chic', 'élégant', 'minimaliste'],
    vintage:     ['bohème', 'romantique', 'casual'],
    edgy:        ['streetwear', 'casual'],
    preppy:      ['chic', 'workwear'],
  };
  for (const s1 of allStyles1) {
    if (compatibles[s1]?.some(c => allStyles2.includes(c))) return 0.70;
  }
  return 0.40;
}

/**
 * Score de partage d'occasions (0..1).
 * Si les 2 articles ont des scores élevés sur la même occasion → match.
 */
function occasionScore(item1, item2) {
  const o1 = getOccasionScores(item1);
  const o2 = getOccasionScores(item2);
  const allOccasions = new Set([...Object.keys(o1), ...Object.keys(o2)]);
  if (allOccasions.size === 0) {
    // Fallback sur les hints texte
    const h1 = extractHintsFromText(item1);
    const h2 = extractHintsFromText(item2);
    if (h1.occasions.some(o => h2.occasions.includes(o))) return 0.8;
    return 0.6;
  }
  let bestMatch = 0;
  for (const occ of allOccasions) {
    const v1 = o1[occ] || 0;
    const v2 = o2[occ] || 0;
    if (v1 >= 60 && v2 >= 60) {
      // Plus les 2 sont élevés, mieux c'est
      const harmonic = (2 * v1 * v2) / (v1 + v2);
      bestMatch = Math.max(bestMatch, harmonic / 100);
    }
  }
  return bestMatch > 0 ? Math.min(1.0, bestMatch + 0.1) : 0.5;
}

/**
 * Score de saisonnalité par rapport à une température donnée (0..1).
 * Pénalise un manteau d'hiver à 25°C ou un débardeur à 0°C.
 */
function seasonScore(item, tempC) {
  if (tempC == null) return 1.0;
  const seasonality = getAttr(item, 'seasonality') || [];
  const tempRange = getAttr(item, 'temperatureRange');

  // Si on a une plage température explicite, on l'utilise en premier
  if (Array.isArray(tempRange) && tempRange.length === 2) {
    const [tmin, tmax] = tempRange;
    if (tempC >= tmin && tempC <= tmax) return 1.0;
    if (tempC >= tmin - 3 && tempC <= tmax + 3) return 0.7;
    return 0.3;
  }

  if (!Array.isArray(seasonality) || seasonality.length === 0) return 0.75;

  const currentSeason =
    tempC >= 22 ? 'été' :
    tempC >= 13 ? 'mi-saison' :
    tempC >= 5  ? 'automne' :
                  'hiver';

  if (seasonality.some(s => s.toLowerCase().includes(currentSeason))) return 1.0;
  // Mi-saison s'accommode souvent de printemps/automne
  if (currentSeason === 'mi-saison' &&
      seasonality.some(s => /printemps|automne/i.test(s))) return 0.85;
  return 0.45;
}

/**
 * Score de cohérence pattern (0..1).
 * Deux imprimés différents en même temps = mismatch (sauf petits/discrets).
 */
function patternScore(item1, item2) {
  const p1 = (getAttr(item1, 'pattern') || 'uni').toLowerCase();
  const p2 = (getAttr(item2, 'pattern') || 'uni').toLowerCase();
  if (p1 === 'uni' || p2 === 'uni') return 1.0;
  if (p1 === p2) return 0.7; // même imprimé répété ok mais pas idéal
  return 0.4; // imprimés différents = mismatch
}

/**
 * Score de cohérence genre (0..1).
 * Pour mélanger F+M : 0.3 (souvent intentionnel mais incohérent par défaut).
 */
function genderScore(item1, item2) {
  const g1 = getAttr(item1, 'gender');
  const g2 = getAttr(item2, 'gender');
  if (!g1 || !g2) return 0.85;
  if (g1 === g2) return 1.0;
  if (g1 === 'unisex' || g2 === 'unisex') return 0.95;
  return 0.4;
}

/**
 * Score de pertinence d'un article par rapport au groupe d'âge user (0..1).
 * Utilise `ai_metadata.ageGroup` (1-5) comme cible :
 *   1 = 18-25 ans   (très jeune, tendance forte)
 *   2 = 25-35 ans   (jeune adulte, mode urbaine)
 *   3 = 35-50 ans   (adulte, élégance accessible)
 *   4 = 50-65 ans   (mature, sobriété raffinée)
 *   5 = 65+ ans     (senior, classiques confortables)
 *   0 = tous âges   (pièce versatile sans cible marquée)
 *
 * Le score dépend de l'écart entre le groupe cible et le groupe user :
 *   - Écart 0 (match parfait)    → 1.0
 *   - Écart 1 (tranche voisine)  → 0.80
 *   - Écart 2                    → 0.55
 *   - Écart 3                    → 0.35
 *   - Écart 4 (extrêmes)         → 0.20
 *   - Article "tous âges" (0)    → 0.90
 *   - Pas d'info IA              → 0.90
 *
 * @param {Object} item       l'article à scorer
 * @param {number} userGroup  groupe d'âge user (1-5), peut être null
 */
function ageScore(item, userGroup) {
  if (userGroup == null) return 1.0;
  const ai = item?.ai_metadata || {};

  // Lecture du ageGroup IA (priorité), sinon fallback sur ageRange string,
  // sinon valeur par défaut neutre.
  let target = ai.ageGroup;

  // Compatibilité ascendante : si l'article a encore l'ancien format ageRange
  if (target == null && typeof ai.ageRange === 'string') {
    const map = { '18-25': 1, '25-35': 2, '35-50': 3, '50-65': 4, '65+': 5, 'tous': 0 };
    target = map[ai.ageRange];
  }

  // Pas d'info → neutre élevé
  if (target == null) return 0.90;

  // Article tagué "tous âges" → bon score quel que soit l'user
  if (target === 0) return 0.90;

  // Validation : on attend 1-5
  if (target < 1 || target > 5) return 0.85;

  const diff = Math.abs(target - userGroup);
  if (diff === 0) return 1.0;
  if (diff === 1) return 0.80;
  if (diff === 2) return 0.55;
  if (diff === 3) return 0.35;
  return 0.20; // diff 4 (groupes 1 vs 5 = très grand écart)
}

/**
 * Score de pertinence d'un article par rapport à la silhouette user (0..1).
 *
 * Source 1 (priorité) : `ai_metadata.compatibleSilhouettes` — array de codes
 *   morpho que Claude Vision a jugés compatibles. Si la silhouette user y est,
 *   score 1.0. Si l'article est tagué ["all"], score 0.95. Sinon 0.4.
 *
 * Source 2 (fallback) : matrice basée sur `ai_metadata.fit` × silhouette.
 *
 * Si pas d'info, retourne 0.90 (neutre élevé).
 */
function silhouetteScore(item, userSilhouette) {
  if (!userSilhouette) return 1.0;
  const ai = item?.ai_metadata || {};

  // ─── Source 1 : compatibleSilhouettes (signal IA explicite) ───
  const compat = Array.isArray(ai.compatibleSilhouettes) ? ai.compatibleSilhouettes : null;
  if (compat && compat.length > 0) {
    if (compat.includes('all')) return 0.95;
    if (compat.includes(userSilhouette)) return 1.0;
    // Article cible des morphos précises mais pas celle de l'user → mismatch
    return 0.40;
  }

  // ─── Source 2 : matrice fit × silhouette (fallback) ───
  const fit = (ai.fit || '').toLowerCase();
  if (!fit) return 0.90;

  const matrix = {
    // ─ HOMMES (mots simples) ─
    mince: {
      slim: 1.0, fitted: 1.0, skinny: 1.0, regular: 0.85, tailored: 0.95,
      loose: 0.65, oversize: 0.55, drapé: 0.70,
    },
    athletique: {
      slim: 0.95, fitted: 0.95, regular: 1.0, tailored: 1.0,
      loose: 0.75, oversize: 0.65, skinny: 0.80, drapé: 0.80,
    },
    normal: {
      slim: 0.85, fitted: 0.90, regular: 1.0, tailored: 1.0,
      loose: 0.85, oversize: 0.80, skinny: 0.75, drapé: 0.85,
    },
    costaud: {
      slim: 0.55, fitted: 0.65, regular: 1.0, tailored: 0.90,
      loose: 0.95, oversize: 0.95, skinny: 0.40, drapé: 0.85,
    },
    forme: {
      slim: 0.45, fitted: 0.55, regular: 0.85, tailored: 0.80,
      loose: 1.0, oversize: 1.0, skinny: 0.30, drapé: 1.0,
    },

    // ─ FEMMES (morphologies classiques) ─
    sablier: {
      slim: 1.0, fitted: 1.0, regular: 0.95, tailored: 0.95,
      loose: 0.65, oversize: 0.55, skinny: 0.95, drapé: 0.85,
    },
    poire: {
      slim: 0.70, fitted: 0.95, regular: 0.95, tailored: 1.0,
      loose: 0.95, oversize: 0.80, skinny: 0.50, drapé: 0.95,
    },
    pomme: {
      slim: 0.50, fitted: 0.55, regular: 0.85, tailored: 0.80,
      loose: 1.0, oversize: 0.95, skinny: 0.40, drapé: 1.0,
    },
    rectangle: {
      slim: 0.85, fitted: 0.95, regular: 0.95, tailored: 0.95,
      loose: 0.85, oversize: 0.80, skinny: 0.85, drapé: 1.0,
    },
    triangle_inv: {
      slim: 0.85, fitted: 0.85, regular: 1.0, tailored: 0.90,
      loose: 0.95, oversize: 0.75, skinny: 0.80, drapé: 0.95,
    },
  };

  const userRow = matrix[userSilhouette];
  if (!userRow) return 0.90;

  for (const [key, score] of Object.entries(userRow)) {
    if (fit.includes(key)) return score;
  }
  return 0.85;
}

/**
 * Calcule un score de matching réaliste entre 2 articles.
 * Retourne un nombre 0..100.
 *
 * Pondération (somme = 1.0) :
 *  - couleur     25% : critère le plus visible
 *  - formality   15% : un blazer ne va pas avec un jogging
 *  - style       13% : cohérence de registre
 *  - occasion    10% : on cible le même contexte d'usage
 *  - pattern      8% : éviter doubles imprimés
 *  - saison       6% : pertinence météo
 *  - genre        5% : cohérence H/F
 *  - âge          9% : adapté à la tranche d'âge user
 *  - silhouette   9% : coupe adaptée à la morpho user
 *
 * @param {Object} item1
 * @param {Object} item2
 * @param {Object} context  { tempC, occasion, userAgeGroup, userSilhouette, dailyPrefs }
 */
export function matchScore(item1, item2, context) {
  if (!item1 || !item2) return 0;
  const tempC = context?.tempC;
  const userAgeGroup   = context?.userAgeGroup;
  const userSilhouette = context?.userSilhouette;

  const cs = colorScore(item1, item2);
  const fs = formalityScore(item1, item2);
  const ss = styleScore(item1, item2);
  const os = occasionScore(item1, item2);
  const ps = patternScore(item1, item2);
  const ws = (tempC != null)
    ? (seasonScore(item1, tempC) + seasonScore(item2, tempC)) / 2
    : 1.0;
  const gs = genderScore(item1, item2);
  const as = (userAgeGroup != null)
    ? (ageScore(item1, userAgeGroup) + ageScore(item2, userAgeGroup)) / 2
    : 1.0;
  // Score morphologie : moyenne des 2 articles vs la silhouette user
  const ms = (userSilhouette)
    ? (silhouetteScore(item1, userSilhouette) + silhouetteScore(item2, userSilhouette)) / 2
    : 1.0;

  const total =
    cs * 0.25 +
    fs * 0.15 +
    ss * 0.13 +
    os * 0.10 +
    ps * 0.08 +
    ws * 0.06 +
    gs * 0.05 +
    as * 0.09 +
    ms * 0.09;

  return Math.round(Math.max(0, Math.min(1, total)) * 100);
}

/* ══════════════════════════════════════════════════════════
   5. SCORE D'UNE TENUE COMPLÈTE
══════════════════════════════════════════════════════════ */

/**
 * Score global d'une tenue (haut+bas+chaussures, ou robe+chaussures).
 * Calcule la moyenne des scores de paires + bonus de complétude + bonus
 * de match avec les préférences du jour (si présentes).
 *
 * @param {Array} items
 * @param {Object} context  { tempC, occasion, dailyPrefs }
 *   dailyPrefs = { styles: [], occasion, budget, categories }
 *   → bonus jusqu'à +10 si l'outfit colle aux préférences déclarées
 */
export function outfitMatchScore(items, context) {
  if (!items || items.length === 0) return 0;
  if (items.length === 1) return 50; // tenue partielle

  // Toutes les paires possibles
  const pairs = [];
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      pairs.push(matchScore(items[i], items[j], context));
    }
  }
  const avgPair = pairs.reduce((a, b) => a + b, 0) / pairs.length;

  // Bonus complétude
  const cats = items.map(i => i.category);
  const hasBase  = cats.includes('Robes') || (cats.includes('Hauts') && cats.includes('Bas'));
  const hasShoes = cats.includes('Chaussures');
  const completenessBonus = (hasBase ? 5 : -10) + (hasShoes ? 3 : -3);

  // Bonus contexte (occasion ciblée)
  let contextBonus = 0;
  if (context?.occasion) {
    const targetOcc = context.occasion.toLowerCase();
    const hits = items.filter(i => {
      const occs = getOccasionScores(i);
      return (occs[targetOcc] || 0) >= 60 ||
             extractHintsFromText(i).occasions.includes(targetOcc);
    }).length;
    contextBonus = Math.min(8, hits * 3);
  }

  // ═══ Bonus préférences du jour (cold start popup) ═══
  // Si l'user a coché un style/occasion/categories et que la tenue les match,
  // on booste le score pour la mettre en avant dans les listes.
  let prefsBonus = 0;
  const dp = context?.dailyPrefs;
  if (dp) {
    // Match style : un des articles a un styleScore > 60 sur un style coché
    if (Array.isArray(dp.styles) && dp.styles.length > 0) {
      const matchesStyle = items.some(i => {
        const ss = getStyleScores(i);
        const hints = extractHintsFromText(i).styles;
        return dp.styles.some(s => (ss[s] || 0) >= 60 || hints.includes(s));
      });
      if (matchesStyle) prefsBonus += 4;
    }
    // Match occasion (si pas déjà ciblée par context.occasion)
    if (dp.occasion && dp.occasion !== context?.occasion) {
      const matchesOcc = items.some(i => {
        const occs = getOccasionScores(i);
        return (occs[dp.occasion] || 0) >= 60 ||
               extractHintsFromText(i).occasions.includes(dp.occasion);
      });
      if (matchesOcc) prefsBonus += 3;
    }
    // Match catégories : au moins un article dans les catégories recherchées
    if (Array.isArray(dp.categories) && dp.categories.length > 0) {
      const hasCat = items.some(i => dp.categories.includes(i.category));
      if (hasCat) prefsBonus += 3;
    }
  }

  return Math.round(Math.max(0, Math.min(100, avgPair + completenessBonus + contextBonus + prefsBonus)));
}

/* ══════════════════════════════════════════════════════════
   6. MATCHING ARTICLE CATALOGUE vs DRESSING
══════════════════════════════════════════════════════════ */

/**
 * Score de pertinence d'un article catalogue par rapport à un dressing.
 * Calcule la moyenne des matchs avec les articles de catégorie complémentaire.
 *
 * Ex: pour un haut catalogue, on regarde les bas + chaussures du dressing.
 */
export function catalogMatchScore(catalogItem, wardrobe, context) {
  if (!catalogItem || !wardrobe || wardrobe.length === 0) return 0;

  const cat = catalogItem.category || getAttr(catalogItem, 'detectedCategory');
  // Catégories complémentaires
  const complementary = {
    Hauts:       ['Bas', 'Chaussures', 'Vestes', 'Manteaux'],
    Bas:         ['Hauts', 'Chaussures', 'Vestes', 'Manteaux'],
    Robes:       ['Chaussures', 'Vestes', 'Manteaux', 'Sacs', 'Bijoux'],
    Vestes:      ['Hauts', 'Bas', 'Robes'],
    Manteaux:    ['Hauts', 'Bas', 'Robes'], // v1.12.204
    Chaussures:  ['Hauts', 'Bas', 'Robes'],
    Sacs:        ['Robes', 'Hauts', 'Bas'],
    Bijoux:      ['Robes', 'Hauts'],
    Accessoires: ['Hauts', 'Bas', 'Robes'],
  };
  const complCats = complementary[cat] || [];
  const candidates = wardrobe.filter(w => complCats.includes(w.category));

  if (candidates.length === 0) return 50; // pas de complémentaire dans le dressing
  const scores = candidates.map(w => matchScore(catalogItem, w, context));
  scores.sort((a, b) => b - a);
  // On prend la moyenne des top 3 (l'article doit bien matcher avec au moins quelques articles)
  const top = scores.slice(0, 3);
  return Math.round(top.reduce((a, b) => a + b, 0) / top.length);
}

/* ══════════════════════════════════════════════════════════
   EXPORTS
══════════════════════════════════════════════════════════ */

export default {
  matchScore,
  outfitMatchScore,
  catalogMatchScore,
  extractHintsFromText,
};
